/**
 * Capa 0 de la Ficha Municipal: identidad DIVIPOLA + metadatos clave
 * (región SGR, categoría, PDET) + párrafo ejecutivo auto-generado por
 * el backend.
 *
 * El párrafo ejecutivo viene precalculado en `score.executive_summary`
 * — si el score todavía está cargando mostramos un skeleton corto. Si
 * el score cargó pero no trae summary (degraded states), no pintamos
 * nada — la card de score downstream ya explica el porqué.
 */

import { type MunicipalityScoreDTO } from "@/lib/api";
import type { MunicipalityProfileDTO } from "@/lib/types";
import { formatDANE } from "@/lib/utils";

export function FichaHeader({
  profile,
  score,
  isLoadingScore,
}: {
  profile: MunicipalityProfileDTO;
  score: MunicipalityScoreDTO | null;
  isLoadingScore: boolean;
}) {
  return (
    <header
      data-testid="ficha-header"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {/* Convención: muni name está en DB como uppercase (HERVEO).
            CSS capitalize convierte a "Herveo" — más legible. */}
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 capitalize">
          {profile.name.toLowerCase()}
        </h1>
        <span className="text-base text-slate-500 capitalize">
          {profile.department.name.toLowerCase()}
        </span>
        <span className="ml-auto rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
          DANE {formatDANE(profile.dane_code)}
        </span>
      </div>

      {/* Sub-label de alcance: hace explícito que ESTA ficha es el
          análisis SGR específicamente. Las convocatorias activas de
          todas las fuentes (SGR + APC + MinTIC + etc.) viven en el
          Radar. Detalle en el modal "¿Cómo se calcula?". */}
      <p className="mt-1 text-xs font-medium text-slate-500">
        Perfil del municipio · Análisis de captación SGR
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
        <span>Región SGR: {profile.department.region_sgr || "—"}</span>
        <span aria-hidden="true">·</span>
        <span>Categoría {profile.category}</span>
        {profile.is_pdet && (
          <>
            <span aria-hidden="true">·</span>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
              PDET
            </span>
          </>
        )}
      </div>

      {/* Párrafo ejecutivo auto-generado. Visualmente separado con
          un borde lateral brand-600 para señalar que es la lectura
          rápida del muni. */}
      {isLoadingScore ? (
        <div
          data-testid="ficha-summary-skeleton"
          className="mt-5 h-14 animate-pulse rounded-md bg-slate-100"
        />
      ) : score?.executive_summary ? (
        <p
          data-testid="ficha-executive-summary"
          className="mt-5 border-l-4 border-brand-600 bg-brand-50/40 px-4 py-3 text-sm leading-relaxed text-slate-700"
        >
          {score.executive_summary}
        </p>
      ) : null}
    </header>
  );
}
